import { Injectable } from '@angular/core';
import { DevEntityService } from '@frontend-site/shared/core';
import { AsEntityTypeNames, ProjectInfo } from '@frontend-site/shared/data';
import { MenuItem } from '../../../models';
import { MENU_IDS } from '../../menu-ids';
import { CorrectionsMenuFactory } from './corrections-menu-factory';

@Injectable()
export class DevelopmentMenuFactory {
    constructor(private readonly devEntityService: DevEntityService, private readonly correctionsMenuFactory: CorrectionsMenuFactory) {}

    create(projectInfo: ProjectInfo, companyId: number): MenuItem | null {
        if (!this.showMenu(projectInfo)) {
            return null;
        }

        const isShowDevelopmentMenu = this.showDevelopmentMenu(projectInfo);
        const isRestrictedApp = projectInfo.isRestrictedApp();

        if (
            (projectInfo.isSalesforceProject() || !isRestrictedApp) &&
            (isShowDevelopmentMenu || projectInfo.isS4Project() || projectInfo.isCleanCoreProject())
        ) {
            const menu: MenuItem[] = [
                isShowDevelopmentMenu
                    ? {
                          id: MENU_IDS.PROJECT.DEV_ENTITY.ALL_DEVELOPMENT,
                          label: this.getDevelopmentLabel(projectInfo),
                          routePath: `/${projectInfo.id}/development`
                      }
                    : {
                          id: MENU_IDS.PROJECT.DEV_ENTITY.WORKSTREAMS,
                          label: `Enumerations.ApplicationModules.WORKSTREAMS`,
                          routePath: `/${projectInfo.id}/requirements`
                      }
            ];

            if (projectInfo.isS4Project() || projectInfo.isS4GreenProject() || projectInfo.isCleanCoreProject() || projectInfo.isSpsNew()) {
                menu.push(this.correctionsMenuFactory.createCorrectionListMenuItem(projectInfo));
                if (!projectInfo.isIncludesAccAnalysisProject() && !projectInfo.isS4GreenProject()) {
                    menu.push(this.correctionsMenuFactory.createCorrectionIssueListMenuItem(projectInfo.id));
                }
            }

            if (!projectInfo.isIncludesAccAnalysisProject() && !projectInfo.isS4GreenProject() && projectInfo.rdxEnabled) {
                menu.push({
                    id: MENU_IDS.PROJECT.DEV_ENTITY.AGILE_BOARD,
                    label: 'Enumerations.ApplicationModules.AGILE_BOARD',
                    routePath: `/tm/${projectInfo.id}/development/board`
                });
            }

            if (
                !projectInfo.isIncludesAccAnalysisProject() &&
                !projectInfo.isS4GreenProject() &&
                !((projectInfo.isS4Project() || projectInfo.isCleanCoreProject()) && !isShowDevelopmentMenu)
            ) {
                menu.push({
                    id: MENU_IDS.PROJECT.DEV_ENTITY.PLANNING_BOARD,
                    label: 'Enumerations.ApplicationModules.PLANNING_BOARD',
                    routePath: `/tm/${projectInfo.id}/releaseDynamix/${companyId}/project/requirements/board`
                });
            }

            const requirementsOrDevelopmentPath = isShowDevelopmentMenu ? 'development' : 'requirements';
            if (
                projectInfo.isS4ConversionProject() ||
                projectInfo.isS4AccelerateProject() ||
                projectInfo.isS4GreenProject() ||
                projectInfo.isCleanCoreProject()
            ) {
                return {
                    id: MENU_IDS.PROJECT.CORRECTIONS.DEFAULT,
                    label: `Enumerations.ApplicationModules.S4_DEVELOPMENT`,
                    routePath: `/${projectInfo.id}/corrections/list`,
                    menu
                };
            } else
                return {
                    id: MENU_IDS.PROJECT.DEV_ENTITY.DEVELOPMENT,
                    label: `Enumerations.ApplicationModules.${
                        projectInfo.isSalesforceProject() ? 'salesforce.DEVELOPMENT' : 'DEVELOPMENT'
                    }`,
                    routePath: `/${projectInfo.id}/${requirementsOrDevelopmentPath}`,
                    menu
                };
        }

        if ((!projectInfo.isSalesforceProject() && !projectInfo.hasActiveEtl && isShowDevelopmentMenu) || !projectInfo.rdxEnabled) {
            const label = this.devEntityService.getDevEntityTranslateKey(AsEntityTypeNames.REQUIREMENT, true);
            return {
                id: MENU_IDS.PROJECT.DEV_ENTITY.REQUIREMENTS,
                label: label,
                routePath: `/tm/${projectInfo.id}/requirements`
            };
        }

        return null;
    }

    private getDevelopmentLabel(projectInfo: ProjectInfo): string {
        return `Enumerations.ApplicationModules.${
            projectInfo.isSalesforceProject()
                ? 'salesforce.ALL_DEVELOPMENT'
                : projectInfo.isIncludesAccAnalysisProject()
                ? 'FIX_PACKAGES'
                : projectInfo.isS4ConversionProject() || projectInfo.isS4AccelerateProject() || projectInfo.isCleanCoreProject()
                ? 'WORKSTREAMS'
                : 'ALL_DEVELOPMENT'
        }`;
    }

    private showMenu(projectInfo: ProjectInfo): boolean {
        return projectInfo.hasModule('ANALYSIS_MANAGEMENT') || !projectInfo.modular;
    }
    private showDevelopmentMenu(projectInfo: ProjectInfo): boolean {
        return (
            projectInfo.isSalesforceProject() ||
            projectInfo.isUnifiedProject() ||
            (projectInfo.rdxEnabled && !projectInfo.testCatalog && !projectInfo.qc) ||
            (!projectInfo.rdxEnabled && projectInfo.isIcr3Project())
        );
    }
}
